import groupTypes from '../common/group-types';

const INVALID_GROUP_VALUES = [null, undefined, 'null', 'undefined', ''];
const LEGACY_PROTECTED_GROUPS = [
  groupTypes.Retail,
  groupTypes.Super,
  groupTypes.eCommerce,
  groupTypes.Audiology,
];

export const getColumnIdFromBinId = (binId) => {
  if (!binId) return null;
  const match = String(binId).trim().toUpperCase().match(/^([A-Z]+)\d+/);
  return match ? match[1] : null;
};

const normalizeGroupTypeId = (groupTypeId) => {
  if (groupTypeId === null || groupTypeId === undefined) return null;
  const normalized = String(groupTypeId).trim();
  return normalized === '' ? null : normalized;
};

const isValidGroupTypeId = (groupTypeId) => {
  const normalized = normalizeGroupTypeId(groupTypeId);
  return !INVALID_GROUP_VALUES.includes(normalized);
};

export const getMappedGroupTypeForBin = (binId, columnMappings = []) => {
  if (!binId || !Array.isArray(columnMappings) || columnMappings.length === 0) {
    return null;
  }

  const columnId = getColumnIdFromBinId(binId);
  if (!columnId) return null;

  const columnMapping = columnMappings.find((mapping) => {
    if (!mapping || !mapping.column_id || !mapping.group_type_id) return false;
    return getColumnIdFromBinId(mapping.column_id) === columnId;
  });

  return columnMapping ? normalizeGroupTypeId(columnMapping.group_type_id) : null;
};

export const isRestrictedByColumnMapping = (binId, columnMappings = [], userGroupType) => {
  const currentUserGroupType = normalizeGroupTypeId(userGroupType);
  const mappedGroupType = getMappedGroupTypeForBin(binId, columnMappings);
  if (!isValidGroupTypeId(mappedGroupType)) {
    return false;
  }

  if (!isValidGroupTypeId(currentUserGroupType)) {
    return true;
  }

  if (currentUserGroupType === groupTypes.Super) {
    return false;
  }

  return mappedGroupType !== currentUserGroupType;
};

export const isBinRestrictedForUser = ({
  binId,
  userGroupType,
  columnMappings = [],
  occupiedGroupTypeId = null,
}) => {
  const currentUserGroupType = normalizeGroupTypeId(userGroupType);
  const mappedGroupType = getMappedGroupTypeForBin(binId, columnMappings);
  if (isValidGroupTypeId(mappedGroupType)) {
    if (!isValidGroupTypeId(currentUserGroupType)) {
      return true;
    }

    if (currentUserGroupType === groupTypes.Super) {
      return false;
    }

    return mappedGroupType !== currentUserGroupType;
  }

  if (!isValidGroupTypeId(currentUserGroupType)) {
    return false;
  }

  if (currentUserGroupType === groupTypes.Super) {
    return false;
  }

  const occupiedGroupType = normalizeGroupTypeId(occupiedGroupTypeId);
  if (!isValidGroupTypeId(currentUserGroupType)) {
    return LEGACY_PROTECTED_GROUPS.includes(occupiedGroupType);
  }

  if (isValidGroupTypeId(occupiedGroupType)) {
    return occupiedGroupType !== currentUserGroupType;
  }

  return false;
};

export const getColumnRestrictionHighlightStyle = (binId, columnMappings = [], userGroupType) => {
  if (!isRestrictedByColumnMapping(binId, columnMappings, userGroupType)) {
    return undefined;
  }

  return {
    backgroundColor: '#FFE0B2',
    border: '2px solid #FF9800',
    /* Hold/Deactivate/Locked status classes use white text; override for contrast on amber */
    color: '#422006',
  };
};

export const shouldShowRestrictionLock = ({
  binId,
  userGroupType,
  columnMappings = [],
  occupiedGroupTypeId = null,
}) =>
  isBinRestrictedForUser({
    binId,
    userGroupType,
    columnMappings,
    occupiedGroupTypeId,
  });

const COLUMN_MAPPING_GROUP_LABELS = {
  [groupTypes.Pharmacy]: 'Pharmacy',
  [groupTypes.Retail]: 'Optical',
  [groupTypes.eCommerce]: 'eCommerce',
  [groupTypes.Audiology]: 'Audiology',
  [groupTypes.Super]: 'Super Center',
};

const formatColumnMappingGroupList = (labels = []) => {
  if (!labels.length) {
    return '';
  }
  if (labels.length === 1) {
    return labels[0];
  }
  if (labels.length === 2) {
    return `${labels[0]} and ${labels[1]}`;
  }
  const last = labels[labels.length - 1];
  return `${labels.slice(0, -1).join(', ')}, and ${last}`;
};

export const getColumnMappingNotice = (columnMappings = [], userGroupType) => {
  const mappings = Array.isArray(columnMappings) ? columnMappings : [];
  const otherGroupLabels = Array.from(new Set(
    mappings
      .filter((mapping) => mapping && mapping.group_type_id && (!userGroupType || mapping.group_type_id !== userGroupType))
      .map((mapping) => COLUMN_MAPPING_GROUP_LABELS[mapping.group_type_id] || 'another group')
  )).filter(Boolean);

  if (mappings.length === 0 || (otherGroupLabels.length === 0 && userGroupType)) {
    return null;
  }

  const activeGroupLabel = userGroupType && COLUMN_MAPPING_GROUP_LABELS[userGroupType]
    ? COLUMN_MAPPING_GROUP_LABELS[userGroupType]
    : null;

  let text = 'Locked bins mark columns reserved by column mapping. Use the unit selector to switch groups when you need them.';
  if (activeGroupLabel && otherGroupLabels.length > 0) {
    text = `You're working in ${activeGroupLabel}. Bins mapped to ${formatColumnMappingGroupList(otherGroupLabels)} show a lock icon and stay read-only here.`;
  } else if (otherGroupLabels.length > 0) {
    text = `Bins mapped to ${formatColumnMappingGroupList(otherGroupLabels)} show a lock icon and stay read-only in this view. Make sure you are assigned to the correct group.`;
  }

  return {
    title: 'Column mapping is active',
    text,
  };
};



// WEBPACK FOOTER //
// ./src/components/assign-prescriptions/columnRestriction.js